/**
 * dateUtils – thin adapter over date-fns / date-fns-tz.
 *
 * All date/time logic in this codebase should go through this module so that
 * the underlying library can be swapped without touching call-sites, and so
 * that "now" can be injected in tests via jest.useFakeTimers / jest.setSystemTime.
 *
 * Moment.js and moment-timezone are BANNED – see the ESLint `no-restricted-imports`
 * rule in eslint.config.mjs.
 */

import {
  addDays,
  addHours,
  addMinutes,
  differenceInCalendarDays,
  differenceInMinutes,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  formatDistanceToNow,
  fromUnixTime,
  getUnixTime,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subHours,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

/** IANA timezone for all inverter sites.  All date/time logic that needs a
 *  site-local clock must use this constant so there is a single place to
 *  change if the requirement ever broadens. */
export const SITE_TIMEZONE = 'Europe/London';

const UK_TZ = SITE_TIMEZONE;

// ---------------------------------------------------------------------------
// Parsing – re-exported for a single import path
// ---------------------------------------------------------------------------

export { fromUnixTime, getUnixTime };

// ---------------------------------------------------------------------------
// Arithmetic – re-exported for a single import path
// ---------------------------------------------------------------------------

export {
  addDays,
  addHours,
  addMinutes,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subHours,
  subMonths,
  subWeeks,
  subYears,
};

// ---------------------------------------------------------------------------
// Comparison – re-exported for a single import path
// ---------------------------------------------------------------------------

export { isAfter, isBefore, isSameDay };

/** Difference in whole calendar days (laterDate minus earlierDate). Positive
 *  when laterDate is after earlierDate. */
export const calendarDaysDiff = (laterDate: Date, earlierDate: Date): number =>
  differenceInCalendarDays(laterDate, earlierDate);

/** Difference in whole minutes (laterDate minus earlierDate). Positive when
 *  laterDate is after earlierDate. */
export const minutesDiff = (laterDate: Date, earlierDate: Date): number => differenceInMinutes(laterDate, earlierDate);

// ---------------------------------------------------------------------------
// Formatting (local system timezone)
// ---------------------------------------------------------------------------

/** Formats a Date as HH:mm in the local system timezone. */
export const formatHHmm = (date: Date): string => format(date, 'HH:mm');

/** Formats a Date as dd/MM/yy in the local system timezone. */
export const formatDDMMYY = (date: Date): string => format(date, 'dd/MM/yy');

/** Formats a Date as dd/MM/yyyy in the local system timezone. */
export const formatDDMMYYYY = (date: Date): string => format(date, 'dd/MM/yyyy');

/** Formats a Date as dd MMMM yyyy (e.g. "15 January 2026") in the local timezone. */
export const formatDDMMMMYYYY = (date: Date): string => format(date, 'dd MMMM yyyy');

/** Formats a Date as yyyy-MM-dd (ISO date, no time) in the local timezone. */
export const formatISODate = (date: Date): string => format(date, 'yyyy-MM-dd');

/** Returns a human-readable relative time string ("2 hours ago", "in 3 days", etc.). */
export const fromNow = (date: Date): string => formatDistanceToNow(date, { addSuffix: true });

/** Formats a Date using an arbitrary date-fns format string in the local
 *  system timezone. Use this only when none of the typed helpers above fits. */
export const formatDate = (date: Date, formatStr: string): string => format(date, formatStr);

// ---------------------------------------------------------------------------
// Generic timezone-parameterized operations
//
// Pass SITE_TIMEZONE (or any IANA identifier) to keep date logic decoupled
// from the browser's local timezone.
// ---------------------------------------------------------------------------

/**
 * Returns a Date whose local calendar fields represent the current time in the given IANA timezone.
 * Note: the underlying timestamp (getTime) is shifted by the timezone offset; use `new Date()` when you need the true current instant.
 */
export const nowInTz = (tz: string): Date => toZonedTime(new Date(), tz);

/**
 * Returns midnight (start of day) in the given IANA timezone as a UTC Date.
 *
 * @param tz   - IANA timezone identifier, e.g. "Europe/London", "Australia/Sydney".
 * @param date - Reference instant; defaults to `new Date()` (now).
 */
export const startOfDayInTz = (tz: string, date: Date = new Date()): Date =>
  fromZonedTime(startOfDay(toZonedTime(date, tz)), tz);

/**
 * Returns end of day (23:59:59.999) in the given IANA timezone as a UTC Date.
 *
 * @param tz   - IANA timezone identifier.
 * @param date - Reference instant; defaults to `new Date()` (now).
 */
export const endOfDayInTz = (tz: string, date: Date = new Date()): Date =>
  fromZonedTime(endOfDay(toZonedTime(date, tz)), tz);

/**
 * Returns true when two Dates fall on the same calendar day in the given timezone.
 *
 * @param tz - IANA timezone identifier.
 */
export const isSameDayInTz = (tz: string, a: Date, b: Date): boolean =>
  isSameDay(toZonedTime(a, tz), toZonedTime(b, tz));

/**
 * Formats a Date as HH:mm in the given IANA timezone.
 *
 * @param tz   - IANA timezone identifier.
 * @param date - The instant to format.
 */
export const formatHHmmInTz = (tz: string, date: Date): string => formatInTimeZone(date, tz, 'HH:mm');

/**
 * Formats a Date using an arbitrary date-fns format string in the given IANA timezone.
 *
 * @param tz      - IANA timezone identifier.
 * @param date    - The instant to format.
 * @param fmtStr  - A date-fns format string (e.g. 'yyyy-MM-dd', 'HH:mm:ss').
 */
export const formatInTz = (tz: string, date: Date, fmtStr: string): string => formatInTimeZone(date, tz, fmtStr);

// ---------------------------------------------------------------------------
// Convenience aliases for SITE_TIMEZONE (Europe/London)
// ---------------------------------------------------------------------------

export const nowUK = (): Date => nowInTz(UK_TZ);
export const startOfUKDay = (date: Date = new Date()): Date => startOfDayInTz(UK_TZ, date);
export const formatHHmmInUK = (date: Date): string => formatHHmmInTz(UK_TZ, date);
export const endOfUKDay = (date: Date = new Date()): Date => endOfDayInTz(UK_TZ, date);
export const isSameUKDay = (a: Date, b: Date): boolean => isSameDayInTz(UK_TZ, a, b);

// ---------------------------------------------------------------------------
// Timestamp normalisation
// ---------------------------------------------------------------------------

/** Normalises a value to Unix seconds. Accepts seconds (~1e9) or milliseconds (~1e12).
 *  Returns null for non-finite or non-parseable values. */
export const normalizeToUnixSeconds = (value: unknown): number | null => {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  if (!Number.isFinite(n)) return null;
  return n > 1_000_000_000_000 ? Math.floor(n / 1000) : Math.floor(n);
};
